import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { request } from '../utils/api';

import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { readToken } from '../auth/session';

/**
 * Single source of unread-notification state for the whole app.
 *
 * The platform bell and the workflow bell used to poll independently, each on
 * its own 30s timer against its own endpoint — two requests, two JWT parses,
 * two filter chains, two transactions, every half minute per signed-in user,
 * just to render two badges.
 *
 * Both now read from here, and this polls GET /api/notifications/summary once.
 * The two systems keep their own shapes in the response: they are stored in
 * different tables, have different fields, and are marked read through
 * different endpoints, so flattening them would lose information the panels
 * still need.
 */

const NotificationSummaryContext = createContext(null);

const EMPTY_SUMMARY = {
  platform: { unreadCount: 0, notifications: [] },
  workflow: { unreadCount: 0, notifications: [] },
  totalUnreadCount: 0,
};

export const NOTIFICATION_POLL_INTERVAL_MS = 30000;

export function NotificationSummaryProvider({ children }) {
  const [summary, setSummary] = useState(EMPTY_SUMMARY);

  const refresh = useCallback(async () => {
    // No token means nobody is signed in — the landing and auth pages mount
    // this provider too, and polling there would only produce 401s.
    if (!readToken()) {
      return;
    }

    try {
      const res = await request('/api/notifications/summary?unreadOnly=true', 'GET');
      if (!res || !res.success) return;

      setSummary({
        platform: {
          unreadCount: res.platform?.unread_count ?? 0,
          notifications: res.platform?.notifications ?? [],
        },
        workflow: {
          unreadCount: res.workflow?.unread_count ?? 0,
          notifications: res.workflow?.notifications ?? [],
        },
        totalUnreadCount: res.total_unread_count ?? 0,
      });
    } catch (error) {
      // Transient failures are expected (offline, token expiring mid-flight).
      // Keep the previous counts rather than flashing the badges to zero.
      console.error('Error fetching notification summary:', error);
    }
  }, []);

  // useVisiblePolling only schedules the interval; without this the badges
  // would stay blank until the first tick 30s after sign-in.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Paused while the tab is hidden, and refires on return — see useVisiblePolling.
  useVisiblePolling(refresh, NOTIFICATION_POLL_INTERVAL_MS);

  const value = useMemo(() => ({ ...summary, refresh }), [summary, refresh]);

  return (
    <NotificationSummaryContext.Provider value={value}>
      {children}
    </NotificationSummaryContext.Provider>
  );
}

/**
 * Returns { platform, workflow, totalUnreadCount, refresh }.
 *
 * Safe to call outside the provider — components then see zero counts and a
 * no-op refresh rather than throwing, which keeps the bells renderable in
 * isolation (tests, storybook).
 */
export function useNotificationSummary() {
  return useContext(NotificationSummaryContext) ?? { ...EMPTY_SUMMARY, refresh: () => {} };
}

export default NotificationSummaryProvider;
